import { Box, Flex, Text } from '@chakra-ui/react'
import { Trans } from '@lingui/react/macro'
import { colors } from '../../theme/cssVariables'
import { TopDrop } from '../../types'
import FlipCard from '../FlipCard'
import DropContainer from './DropContainer'
import DropTitle from './DropTitle'
import Front from './Front'
import React, { useCallback, useEffect, useRef } from 'react'
import { debounce, throttle } from 'lodash'
import Back from './Back'
import gsap from 'gsap'

const LiveDrops = React.memo(
  ({
    liveDataValues,
    isMobile,
    isliveOnHover,
    setIssliveOnHover,
    dropItemWidth,
    timelineRef,
  }: {
    liveDataValues: {
      skins: TopDrop[]
      newEventsCount: number
    }
    isMobile: boolean
    isliveOnHover: boolean
    setIssliveOnHover: (isliveOnHover: boolean) => void
    dropItemWidth: number
    timelineRef: any
  }) => {
    const liveAniContainerRef = useRef<HTMLDivElement>(null)
    const dropTimelineRef = useRef<gsap.core.Timeline | null>(null)
    const savedTranslateProgressRef = useRef<number>(0)

    // live 动画
    useEffect(() => {
      if (!liveDataValues.newEventsCount) return
      const newEventsLength = liveDataValues.newEventsCount
      if (newEventsLength === 0) return
      const container = liveAniContainerRef.current
      const ctx = gsap.context(() => {
        if (container) {
          // 1. 平移动画时间线
          timelineRef.current = gsap.timeline().fromTo(
            container,
            { translateX: -(dropItemWidth + 1) * liveDataValues.newEventsCount },
            {
              translateX: 0,
              duration: 0.8,
              ease: 'power2.out',
              onComplete: () => {
                gsap.set(container, { clearProps: 'translateX' })
              },
            },
          )
          // 2. 掉落动画时间线
          const boxesToAdd = gsap.utils
            .toArray<HTMLElement>('.live-item', container)
            .slice(0, newEventsLength)
          dropTimelineRef.current = gsap.timeline().fromTo(
            boxesToAdd,
            { y: -300 },
            {
              y: 0,
              duration: 0.3,
              stagger: -0.08,
              ease: 'power2.out',
              onComplete: () => {
                gsap.set(boxesToAdd, { clearProps: 'y' })
              },
            },
          )
        }
      }, container ?? undefined)

      return () => {
        ctx.revert()
        timelineRef.current = null
        dropTimelineRef.current = null
        gsap.set(container, { translateX: 0 })
        gsap.set('.live-item', { y: 0, clearProps: 'all' })
      }
    }, [liveDataValues.skins, liveDataValues.newEventsCount])

    // hover控制live动画
    useEffect(() => {
      const translateTl = timelineRef.current

      if (isliveOnHover) {
        if (translateTl) {
          translateTl.pause()
          savedTranslateProgressRef.current = translateTl.progress()
        }
      } else {
        requestAnimationFrame(() => {
          if (!translateTl) return
          if (translateTl.progress() === 1) return
          translateTl.progress(savedTranslateProgressRef.current || 0)
          translateTl.play()
        })
      }
    }, [isliveOnHover])

    const throttledSetLiveHover = useCallback(
      (val: boolean) => {
        throttle((value: boolean) => setIssliveOnHover(value), 0)(val)
      },
      [setIssliveOnHover],
    )

    const debouncedLiveClose = useCallback(() => {
      debounce(() => setIssliveOnHover(false), 0)()
    }, [setIssliveOnHover])
    // live hover
    const liveHoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
    const handleLiveMouseEnter = () => {
      if (liveHoverTimeout.current) {
        clearTimeout(liveHoverTimeout.current)
        liveHoverTimeout.current = null
      }
      throttledSetLiveHover(true)
    }
    const handleLiveMouseLeave = () => {
      if (liveHoverTimeout.current) {
        clearTimeout(liveHoverTimeout.current)
      }
      liveHoverTimeout.current = setTimeout(() => {
        debouncedLiveClose()
      }, 0)
    }

    return (
      <Box position={'relative'} width={'100%'}>
        <DropTitle color={colors.colorB2} pl={0}>
          <Text
            fontSize={['xs', 'sm']}
            width={'fit-content'}
            textTransform={'uppercase'}
            whiteSpace={'nowrap'}
          >
            <Trans>live drops</Trans>
          </Text>
        </DropTitle>
        <DropContainer>
          <Flex
            onMouseEnter={handleLiveMouseEnter}
            onMouseLeave={handleLiveMouseLeave}
            position={'absolute'}
            top={0}
            left={0}
            right={0}
            bottom={0}
          >
            <Flex className="live-ani-container" gap={'1px'} ref={liveAniContainerRef}>
              {liveDataValues.skins?.map((_: TopDrop, index: number) => {
                return (
                  <FlipCard
                    key={`live-${_.instanceId}`}
                    isLoaded={true}
                    front={<Front {..._} isMobile={isMobile} />}
                    back={<Back {..._} />}
                    containerStyle={{
                      width: dropItemWidth,
                      minW: 0,
                      className: 'live-item',
                      flex: 'none',
                    }}
                  />
                )
              })}
            </Flex>
          </Flex>
        </DropContainer>
      </Box>
    )
  },
)

export default LiveDrops
